import { useEffect, useState } from "react";

import discord_msg_reactions from "../../../types/discord_msg_reactions";
import discord_parent_msgs from "../../../types/discord_parent_msgs";
import discord_thread_msgs from "../../../types/discord_thread_msgs";
import { getSupaClient } from "../../lib/supabase";

/**
 * Loads number of messages, reactions and counts for a given channel and
 * time period
 * @param startTime - start of period
 * @param endTime - end of period
 * @param platform - "discord" or "slack"
 * @param channelId (optional) The channel id to filter by
 * @returns
 * - parentCount: total parent messages
 * - reactionCount: total reactions
 * - threadMessageCount: total thread messages
 * - loading: wether the hook is still loading
 */
function useQuickEngagement(
  startTime: string,
  endTime: string,
  // teamId: string,
  platform: string,
  channelId?: string
) {
  const supabase = getSupaClient();
  const [loading, setLoading] = useState<boolean>(false);
  const [parentCount, setParentCount] = useState<number>(0);
  const [reactionCount, setReactionCount] = useState<number>(0);
  const [threadMessageCount, setThreadMessageCount] = useState<number>(0);

  // Load Discord parent messages count
  const loadDiscordParentMessageCount = async () => {
    let base = supabase
      .from<discord_parent_msgs>("discord_parent_msgs")
      .select("*", { count: "exact" })
      .lte("created_at_discord", endTime)
      .gte("created_at_discord", startTime);

    if (channelId) {
      base = base.filter("channel", "eq", channelId);
    } else {
      console.log("NO CHANNEL ID!");
    }

    const { data, error, count } = await base;

    if (error) {
      console.log("Error in loadDiscordParentMessageCount:", error);
    }
    setParentCount(count || 0);
  };

  // Load Discord reaction count
  const loadDiscordReactionCount = async () => {
    let base = supabase
      .from<discord_msg_reactions>("discord_msg_reactions")
      .select("*", { count: "exact" })
      .lte("created_at_discord", endTime)
      .gte("created_at_discord", startTime);

    if (channelId) {
      base = base.filter("channel", "eq", channelId);
    }
    const { data, error, count } = await base;

    if (error) {
      console.log("Error in loadDiscordReactionCount:", error);
    }
    setReactionCount(count || 0);
  };

  // Load Discord thread message count
  const loadDiscordThreadMessageCount = async () => {
    let base = supabase
      .from<discord_thread_msgs>("discord_thread_msgs")
      .select("*", { count: "exact" })
      .lte("created_at_discord", endTime)
      .gte("created_at_discord", startTime);

    if (channelId) {
      base = base.filter("discord_channel", "eq", channelId);
    }
    const { data, error, count } = await base;
    if (error) {
      console.log("Error in loadDiscordThreadMessageCount:", error);
    }
    setThreadMessageCount(count || 0);
  };

  const loadDiscordMetrics = async () => {
    setLoading(true);
    await loadDiscordParentMessageCount();
    await loadDiscordReactionCount();
    await loadDiscordThreadMessageCount();
    setLoading(false);
  };

  useEffect(() => {
    if (platform === "discord") {
      loadDiscordMetrics();
    } else {
      console.log("Loading Slack metrics!"); // <-- TODO
    }
  }, [startTime, endTime, channelId]);

  return {
    parentCount,
    reactionCount,
    threadMessageCount,
    loading,
  };
}

export default useQuickEngagement;
